import { apiClient } from './apiClient';

export interface Group {
  id: string;
  displayName: string;
  description?: string;
  mail?: string;
  mailEnabled: boolean;
  securityEnabled: boolean;
  groupTypes: string[];
  createdDateTime?: string;
}

export interface GroupMember {
  id: string;
  displayName: string;
  userPrincipalName: string;
  mail?: string;
}

export interface CreateGroupRequest {
  displayName: string;
  description?: string;
  mailNickname: string;
  mailEnabled: boolean;
  securityEnabled: boolean;
  groupTypes: string[];
}

export interface GroupsListResponse {
  success: boolean;
  data: {
    value: Group[];
    '@odata.count'?: number;
  };
}

export interface GroupResponse {
  success: boolean;
  data: Group;
}

export interface GroupMembersResponse {
  success: boolean;
  data: {
    value: GroupMember[];
  };
}

export const groupService = {
  async getGroups(params?: {
    top?: number;
    skip?: number;
    filter?: string;
    search?: string;
  }): Promise<GroupsListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.top) queryParams.append('$top', params.top.toString());
    if (params?.skip) queryParams.append('$skip', params.skip.toString());
    if (params?.filter) queryParams.append('$filter', params.filter);
    if (params?.search) queryParams.append('$search', params.search);

    return apiClient.get<GroupsListResponse>(`/groups?${queryParams.toString()}`);
  },

  async getGroupById(groupId: string): Promise<GroupResponse> {
    return apiClient.get<GroupResponse>(`/groups/${groupId}`);
  },

  async createGroup(group: CreateGroupRequest): Promise<GroupResponse> {
    return apiClient.post<GroupResponse>('/groups', group);
  },

  async updateGroup(groupId: string, updates: Partial<CreateGroupRequest>): Promise<GroupResponse> {
    return apiClient.patch<GroupResponse>(`/groups/${groupId}`, updates);
  },

  async deleteGroup(groupId: string): Promise<void> {
    return apiClient.delete(`/groups/${groupId}`);
  },

  async getGroupMembers(groupId: string): Promise<GroupMembersResponse> {
    return apiClient.get<GroupMembersResponse>(`/groups/${groupId}/members`);
  },

  async addMember(groupId: string, userId: string): Promise<void> {
    return apiClient.post(`/groups/${groupId}/members`, { userId });
  },

  async removeMember(groupId: string, userId: string): Promise<void> {
    return apiClient.delete(`/groups/${groupId}/members/${userId}`);
  },
};
